/**
 * Marketplace Sales Section for TransactionAnalyzer
 * Contains: Sales by Country, Cost Distribution by Country, FBA Cost Breakdown, Postal Zones
 */

import React from 'react';
import type { MarketplaceCode, MarketplaceConfig } from '../../types/transaction';
import { MARKETPLACE_CONFIGS, ZONE_NAMES } from '../../constants/marketplaces';
import { REGION_MAP_SUPPORTED } from '../../constants/postalToRegion';
import { PieChart } from './PieChart';
import TopStatesSection from './TopStatesSection';
import type { DetailedAnalytics } from '../../services/analytics/analyticsEngine';

interface MarketplaceSalesSectionProps {
  analytics: DetailedAnalytics;
  comparisonAnalytics: DetailedAnalytics | null;
  marketplaceCode: MarketplaceCode | null;
  config: MarketplaceConfig;
  selectedFulfillment: string;
  formatMoney: (value: number) => string;
  onOpenComparisonDetail: (type: 'fbaCostBreakdown') => void;
}

const MarketplaceSalesSection: React.FC<MarketplaceSalesSectionProps> = ({
  analytics,
  comparisonAnalytics,
  marketplaceCode,
  config,
  selectedFulfillment,
  formatMoney,
  onOpenComparisonDetail,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 print-single-col">
        {/* Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 print-card print-block">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 print-header">
            Income Distribution (Order Sales = 100%)
          </h3>
          <PieChart
            data={analytics.pieChartData.values}
            labels={analytics.pieChartData.labels}
            colors={analytics.pieChartData.colors}
            comparisonData={comparisonAnalytics ? (() => {
              if (selectedFulfillment === 'FBA') {
                const baseCosts = [
                  comparisonAnalytics.fbaSellingFees,
                  comparisonAnalytics.totalFbaFees,
                  comparisonAnalytics.totalFBACost,
                  comparisonAnalytics.fbaAdvertisingCost,
                  comparisonAnalytics.actualRefundLoss
                ];
                if (config?.vatIncludedInPrice) baseCosts.push(comparisonAnalytics.totalVAT);
                const netRemaining = Math.max(0, comparisonAnalytics.fbaOrderSales - baseCosts.reduce((sum: number, val: number) => sum + val, 0));
                baseCosts.push(netRemaining);
                return baseCosts;
              } else if (selectedFulfillment === 'FBM') {
                const baseCosts = [
                  comparisonAnalytics.fbmSellingFees,
                  comparisonAnalytics.totalFBMCost,
                  comparisonAnalytics.fbmAdvertisingCost,
                  comparisonAnalytics.actualRefundLoss
                ];
                if (config?.vatIncludedInPrice) baseCosts.push(comparisonAnalytics.totalVAT);
                const netRemaining = Math.max(0, comparisonAnalytics.fbmOrderSales - baseCosts.reduce((sum: number, val: number) => sum + val, 0));
                baseCosts.push(netRemaining);
                return baseCosts;
              } else {
                const baseCosts = [
                  comparisonAnalytics.totalSellingFees,
                  comparisonAnalytics.totalFbaFees,
                  comparisonAnalytics.totalFBACost,
                  comparisonAnalytics.totalFBMCost,
                  comparisonAnalytics.advertisingCost,
                  comparisonAnalytics.actualRefundLoss
                ];
                if (config?.vatIncludedInPrice) baseCosts.push(comparisonAnalytics.totalVAT);
                const netRemaining = Math.max(0, comparisonAnalytics.totalSales - baseCosts.reduce((sum: number, val: number) => sum + val, 0));
                baseCosts.push(netRemaining);
                return baseCosts;
              }
            })() : undefined}
            comparisonTotalSales={comparisonAnalytics ? (
              selectedFulfillment === 'FBA' ? comparisonAnalytics.fbaOrderSales :
              selectedFulfillment === 'FBM' ? comparisonAnalytics.fbmOrderSales :
              comparisonAnalytics.totalSales
            ) : undefined}
            formatMoney={formatMoney}
          />
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Total Order Sales: {formatMoney(analytics.totalSales)}
            </p>
          </div>
        </div>

        {/* Sales by Country - Only in All mode */}
        {!marketplaceCode && analytics.marketplaceSalesDistribution && Object.keys(analytics.marketplaceSalesDistribution).length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 print-card print-block">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 print-header">
              Sales by Country
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.marketplaceSalesDistribution)
                .sort((a, b) => b[1].sales - a[1].sales)
                .map(([code, data]) => {
                  const configData = MARKETPLACE_CONFIGS[code as MarketplaceCode];
                  const percentage = ((data.sales / analytics.totalSales) * 100).toFixed(1);

                  const comparisonData = comparisonAnalytics?.marketplaceSalesDistribution?.[code];
                  const prevSales = comparisonData?.sales || 0;
                  const salesChange = prevSales > 0 ? ((data.sales - prevSales) / prevSales) * 100 : 0;

                  return (
                    <div key={code} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {configData ? configData.name : code}
                        </span>
                        <span className="text-xs text-slate-500">({data.orders} orders)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-800">
                          {formatMoney(data.sales)}
                        </span>
                        <span className="text-xs text-slate-500 w-12 text-right">
                          {percentage}%
                        </span>
                        {comparisonAnalytics && prevSales > 0 && (
                          <span className={`text-xs font-medium w-16 text-right ${salesChange > 0 ? 'text-green-600' : salesChange < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                            {salesChange > 0 ? '\u2191' : salesChange < 0 ? '\u2193' : ''}
                            {Math.abs(salesChange).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* FBA Cost Breakdown - When marketplace selected */}
        {marketplaceCode && (selectedFulfillment === 'all' || selectedFulfillment === 'FBA') && (
          <div className="bg-white rounded-xl shadow-sm p-6 print-card print-block">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">FBA Cost Kırılımı</h3>
              {comparisonAnalytics && (
                <button
                  onClick={() => onOpenComparisonDetail('fbaCostBreakdown')}
                  className="no-print px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                >
                  \ud83d\udcca Detay
                </button>
              )}
            </div>
            <div className="space-y-3">
              {[
                { label: 'Adjustment', value: analytics.adjustmentTotal },
                { label: 'FBA Inventory Fee', value: analytics.inventoryTotal },
                { label: 'Chargeback Refund', value: analytics.chargebackTotal },
                { label: 'Service Fee', value: analytics.serviceTotal },
                { label: 'FBA fees (Other)', value: analytics.fbaOtherFeeTotal },
                { label: 'FBA Transaction Fee', value: analytics.fbaTransactionTotal },
                { label: 'Fee Adjustment', value: analytics.feeAdjustments },
                { label: 'SAFE-T Reimbursement', value: analytics.safetReimbursements },
                { label: 'Liquidations', value: analytics.liquidationsTotal },
              ].filter(item => item.value !== 0).map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{item.label}</span>
                  <span className="text-sm font-semibold text-slate-800">
                    {formatMoney(item.value)}
                  </span>
                </div>
              ))}
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Total FBA Cost</span>
                  <span className="text-lg font-bold text-orange-600">
                    {formatMoney(analytics.totalFBACost)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cost Distribution by Country - Only in All mode */}
      {!marketplaceCode && analytics.marketplacePieCharts && Object.keys(analytics.marketplacePieCharts).length > 0 && (
        <div className="mb-6 print:break-inside-avoid">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 print:text-base print:mb-2">Cost Distribution by Country</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-2 print:gap-2">
            {Object.entries(analytics.marketplacePieCharts)
              .sort((a, b) => b[1].totalSales - a[1].totalSales)
              .map(([mpCode, chartData]) => {
                const mpConfig = MARKETPLACE_CONFIGS[mpCode as MarketplaceCode];
                if (!mpConfig) return null;

                const prevChartData = comparisonAnalytics?.marketplacePieCharts?.[mpCode];
                const prevSales = prevChartData?.totalSales || 0;
                const salesChange = prevSales > 0 ? ((chartData.totalSales - prevSales) / prevSales) * 100 : 0;

                return (
                  <div key={mpCode} className="bg-white rounded-xl shadow-sm p-4 print:p-2 print:shadow-none print:border print:border-slate-200 flex flex-col print:break-inside-avoid">
                    <h3 className="text-base font-semibold text-slate-800 mb-3 print:text-xs print:mb-1 print:text-center">
                      {mpConfig.name}
                    </h3>
                    <div className="flex justify-center flex-grow">
                      <div style={{ transform: 'scale(0.7)', transformOrigin: 'center' }} className="print:!transform-none print:scale-50">
                        <PieChart
                          data={chartData.values}
                          labels={chartData.labels}
                          colors={chartData.colors}
                          formatMoney={formatMoney}
                        />
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-200 print:mt-1 print:pt-1">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 print:text-[9px]">
                          Total Sales: {formatMoney(chartData.totalSales)}
                        </p>
                        {comparisonAnalytics && prevSales > 0 && (
                          <p className={`text-xs font-medium mt-1 ${salesChange > 0 ? 'text-green-600' : salesChange < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                            {salesChange > 0 ? '\u2191' : salesChange < 0 ? '\u2193' : ''}
                            {Math.abs(salesChange).toFixed(1)}% vs prev
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Region Distribution with Choropleth Map (US/CA/UK) */}
      {config?.code && REGION_MAP_SUPPORTED.includes(config.code) && analytics.stateDistribution && Object.keys(analytics.stateDistribution).length > 0 && (
        <TopStatesSection
          marketplace={config.code}
          stateDistribution={analytics.stateDistribution}
          comparisonStateDistribution={comparisonAnalytics?.stateDistribution || null}
          formatMoney={formatMoney}
        />
      )}

      {/* Other marketplaces: Postal Zone Distribution */}
      {config?.code && !REGION_MAP_SUPPORTED.includes(config.code) && config?.hasPostalZones && analytics.postalZones && Object.keys(analytics.postalZones).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 print-block print-card">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Postal Zone Distribution (Top 10)</h3>
          <div className="space-y-3">
            {Object.entries(analytics.postalZones)
              .sort((a, b) => b[1].count - a[1].count)
              .slice(0, 10)
              .map(([zone, data]) => {
                const zoneName = ZONE_NAMES[(config?.code || 'US') as MarketplaceCode]?.[zone] || `Zone ${zone}`;
                const percentage = ((data.count / analytics.totalOrders) * 100).toFixed(1);
                return (
                  <div key={zone} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm font-medium text-slate-700">{zoneName}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-base font-bold text-slate-800">
                        {data.count.toLocaleString()}
                      </span>
                      <span className="text-xs text-slate-500 w-12 text-right">
                        ({percentage}%)
                      </span>
                      <span className="text-sm font-semibold text-blue-600 w-24 text-right">
                        {formatMoney(data.sales)}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceSalesSection;
